'use client';

import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { cn } from '@ps/ui/utils';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import {
  linkNodeHash,
  linkNodePathFromHash,
  linkNodeSelector,
} from '../../linking/functions/linkNodePath';
import { setPendingLinkJump } from '../../linking/functions/pendingLinkJump';
import { scrollToLinkNode } from '../../linking/functions/scrollToLinkNode';
import type { LinkBarItemViewModel } from '../types/LinkBarItemViewModel';

type AppRouter = ReturnType<typeof useRouter>;

/** Whether an element is actually rendered (not in a `display:none` subtree). */
function isVisible(element: Element): boolean {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility();
  }
  return element.getClientRects().length > 0;
}

/**
 * Click handler for a shortcut anchor. The `<a href>` is always the SHAREABLE
 * URL (fragment or deep page); a plain click enhances it with soft-nav + scroll,
 * while modifier / non-primary clicks (open-in-new-tab) fall through to the href
 * untouched.
 *
 * A no-fragment href (entity/deep page, parent tab route) just lets `<Link>`
 * navigate. For a fragment (in-page scroll) target:
 *   - RENDERED on the CURRENTLY VISIBLE page (a custom button in the persistent
 *     profile card, or a section on the active tab) → scroll in place, reflecting
 *     the shareable `#…` via the History API. (`checkVisibility` is load-bearing:
 *     Next keeps visited tabs mounted-but-hidden via `<Activity>`, so a match in a
 *     hidden sibling tab must NOT count as on-page.)
 *   - Same tab but not rendered → deeper infinite-scroll page → go to its deep
 *     page.
 *   - Different tab → CLEAN `router.push` to the tab route (no fragment, so Next's
 *     router state stays clean and a later tab click clears cleanly), carrying the
 *     scroll target + fallback via a one-shot pending jump; `useBioHashScroll`
 *     scrolls and restores the shareable fragment on arrival.
 */
function handleItemClick(
  event: MouseEvent<HTMLAnchorElement>,
  item: LinkBarItemViewModel,
  router: AppRouter,
): void {
  const hashIndex = item.href.indexOf('#');
  if (hashIndex < 0) {
    return;
  }
  const anchorPath = linkNodePathFromHash(item.href.slice(hashIndex));
  if (anchorPath == null) {
    return;
  }
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();

  const targets = document.querySelectorAll(linkNodeSelector(anchorPath));
  if (Array.from(targets).some(isVisible)) {
    window.history.replaceState(
      window.history.state,
      '',
      linkNodeHash(anchorPath),
    );
    scrollToLinkNode(anchorPath);
    return;
  }

  const destinationPath = item.href.slice(0, hashIndex);
  if (destinationPath === window.location.pathname) {
    if (item.fallbackHref != null) {
      router.push(item.fallbackHref);
    }
    return;
  }
  setPendingLinkJump({ anchorPath, fallbackHref: item.fallbackHref });
  router.push(destinationPath);
}

interface Props {
  items: LinkBarItemViewModel[];
  /** Resolved item title style (includes the tint color). */
  titleStyle: CSSProperties;
  /** General container customization, merged onto the root. */
  className?: string;
  /**
   * Open-ended leading slot rendered inside the scroller, before the items
   * (e.g. the editor's "manage tabs" pencil). The view stays unaware of intent.
   */
  children?: ReactNode;
  /**
   * Behavior slot: when provided, each shortcut renders as a `<button>` that
   * calls this instead of a navigational `<a href>`. The editor uses it to jump
   * INTERIOR to the target (its own in-place preview) rather than navigating to
   * the visitor URL. Omitted on the visitor path → the crawlable/shareable `<a>`
   * with soft-nav + scroll ({@link handleItemClick}).
   */
  onItemActivate?: (item: LinkBarItemViewModel) => void;
}

interface ScrollState {
  /** Content overflows the container — i.e. there's somewhere to scroll. */
  overflow: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

const NO_SCROLL: ScrollState = {
  overflow: false,
  canScrollLeft: false,
  canScrollRight: false,
};

/**
 * Bio "link bar" — a horizontal row of shortcut anchors (each points at a
 * content node: collection / series / external URL).
 *
 * Shared component for both bio paths. It's `'use client'` because the
 * scroll-arrow buttons are a built-in interaction for everyone (visitor and
 * owner) — but it's still prop-driven with no context/fetch/edit intent. The
 * anchors are plain `<a>` (SSR'd + crawlable) and the row scrolls natively. The
 * arrow buttons appear only when the content overflows the container, and each
 * is disabled once that direction is fully scrolled.
 *
 * Renders tight — vertical spacing between this and neighbouring sections is the
 * host orchestrator's concern (layout-agnostic).
 */
export function LinkBarView({
  items,
  titleStyle,
  className,
  children,
  onItemActivate,
}: Props) {
  const router = useRouter();
  const color = titleStyle.color as string | undefined;
  // Selected shortcut renders semiBold (weight 600) — 1:1 with legacy
  // `ShortcutLinkBar`, whose `activeLinkId` starts `undefined` (nothing bold on
  // load) and is set on TAP (`LinkBarItem` `family={active ? 'semiBold' :
  // 'regular'}`). This is tap-driven selection, NOT route match — a
  // pathname-derived active wrongly bolded the shortcut pointing at the current
  // page (QA: "Content Guide" heavier on load).
  const [activeId, setActiveId] = useState<string>();
  const styleFor = (id: string): CSSProperties =>
    id === activeId ? { ...titleStyle, fontWeight: 600 } : titleStyle;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<ScrollState>(NO_SCROLL);

  // Track overflow + edge positions so the arrows can hide when everything
  // fits and disable at each end. Re-measures on scroll, container resize, and
  // when the item set changes.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }
    const measure = () => {
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      setScroll({
        overflow: maxScrollLeft > 1,
        canScrollLeft: scroller.scrollLeft > 1,
        canScrollRight: scroller.scrollLeft < maxScrollLeft - 1,
      });
    };
    measure();
    scroller.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [items.length]);

  const scrollByDirection = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * scroller.clientWidth * 0.6,
      behavior: 'smooth',
    });
  };

  return (
    <div data-bio-link-bar className={cn('flex w-full flex-col', className)}>
      {scroll.overflow ? (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => scrollByDirection(-1)}
            disabled={!scroll.canScrollLeft}
            aria-label="Scroll tabs left"
            className="flex h-6 w-6 items-center justify-center rounded-full transition hover:opacity-70 disabled:cursor-default disabled:opacity-30"
            style={{ color }}
          >
            <ChevronLeftIcon className="h-6 w-6" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => scrollByDirection(1)}
            disabled={!scroll.canScrollRight}
            aria-label="Scroll tabs right"
            className="flex h-6 w-6 items-center justify-center rounded-full transition hover:opacity-70 disabled:cursor-default disabled:opacity-30"
            style={{ color }}
          >
            <ChevronRightIcon className="h-6 w-6" aria-hidden />
          </button>
        </div>
      ) : null}

      <div
        ref={scrollerRef}
        role="tablist"
        className={cn(
          'scrollbar-hide flex w-full items-center gap-6 overflow-x-auto',
          // Hide native scrollbar (Chrome/Edge/Safari + Firefox/IE).
          '[-ms-overflow-style:none] [scrollbar-width:none]',
          '[&::-webkit-scrollbar]:hidden',
        )}
      >
        {children}
        {items.map(item =>
          onItemActivate ? (
            <button
              key={item.id}
              type="button"
              role="tab"
              onClick={() => {
                setActiveId(item.id);
                onItemActivate(item);
              }}
              className={cn(
                'shrink-0 whitespace-nowrap capitalize transition hover:opacity-70',
                item.className,
              )}
              style={styleFor(item.id)}
            >
              {item.label}
            </button>
          ) : (
            <Link
              key={item.id}
              role="tab"
              href={item.href}
              onClick={event => {
                setActiveId(item.id);
                handleItemClick(event, item, router);
              }}
              className={cn(
                'shrink-0 whitespace-nowrap capitalize transition hover:opacity-70',
                item.className,
              )}
              style={styleFor(item.id)}
            >
              {item.label}
            </Link>
          ),
        )}
      </div>
    </div>
  );
}
